import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Tag, Plus, RefreshCw, Database } from 'lucide-react';
import ERPAdvancedGrid    from './erp/ERPAdvancedGrid';
import useERPContextMenu  from './erp/ERPContextMenu';
import { erpToast }       from './erp/ERPToast';
import '../styles/erp-theme.css';
import { API, apiFetch } from '../lib/api';

const COLUMNS = [
  { field: 'LicenseTier',        header: 'Tier',          visible: true, frozen: true },
  { field: 'LicenseType',        header: 'Type',          visible: true },
  { field: 'DbModel',            header: 'DB Model',      visible: true },
  { field: 'BasePrice',          header: 'Base \u20b9',        visible: true },
  { field: 'PricePerUser',       header: '\u20b9 / User',      visible: true },
  { field: 'PricePerSystem',     header: '\u20b9 / System',    visible: true },
  { field: 'SupportPrice',       header: 'Support \u20b9',     visible: false },
  { field: 'SupportPercentage',  header: 'Support %',     visible: true },
  { field: 'MaxUsers',           header: 'Max Users',     visible: true },
  { field: 'MaxDevices',         header: 'Max Devices',   visible: true },
  { field: 'MaxDays',            header: 'Max Days',      visible: true },
  { field: 'Features',           header: 'Features',      visible: true },
  { field: 'IsFree',             header: 'Free',          visible: false },
  { field: 'IsSalesDefined',     header: 'Sales-Defined', visible: false },
  { field: 'IsActive',           header: 'Active',        visible: true },
  { field: 'Currency',           header: 'Currency',      visible: false },
  { field: 'TierDescription',    header: 'Description',   visible: false },
  { field: '_actions',           header: 'Actions',       visible: true, sortable: false, filterable: false }
];

/**
 * LicensePricingManager — full CRUD over WebApp.LicensePricing.
 * Uses ERPAdvancedGrid + context menu + modal.
 * Backend endpoints: GET /api/licensor/pricing, POST /api/licensor/pricing,
 * PUT /api/licensor/row/LicensePricing/:id, DELETE /api/licensor/pricing/:id
 */
export default function LicensePricingManager() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('BasePrice');
  const [sortDir, setSortDir]     = useState('asc');
  const [filters, setFilters]     = useState({});

  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`${API}/licensor/pricing`)
      .then(r => r.json())
      .then(d => {
        if (d?.error) setError(d.error);
        else {
          const list = Array.isArray(d) ? d : (d?.pricing || []);
          setRows(list);
          setError(null);
        }
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredSorted = useMemo(() => {
    let r = rows;
    const af = Object.entries(filters).filter(([, v]) => v != null && String(v).trim() !== '');
    if (af.length) {
      r = r.filter(row => af.every(([k, v]) => String(row[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    }
    if (sortField) {
      r = [...r].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return r;
  }, [rows, filters, sortField, sortDir]);

  const total = filteredSorted.length;
  const paginated = useMemo(() => filteredSorted.slice((page - 1) * pageSize, page * pageSize), [filteredSorted, page, pageSize]);

  const onSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
  };

  const startAdd       = useCallback(() => setEditing({ mode: 'add', row: null }), []);
  const startEdit      = useCallback((row) => setEditing({ mode: 'edit', row }), []);
  const startView      = useCallback((row) => setEditing({ mode: 'view', row }), []);
  const startDuplicate = useCallback((row) => setEditing({ mode: 'duplicate', row }), []);

  const onDelete = useCallback(async (row) => {
    if (!window.confirm(`Deactivate tier "${row.LicenseTier}"?\nThis sets IsActive=0 (soft delete).`)) return;
    try {
      const res = await apiFetch(`${API}/licensor/pricing/${encodeURIComponent(row.PricingId)}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || data.error) erpToast.error(`Delete failed: ${data.error || res.status}`);
      else { erpToast.success(`Tier "${row.LicenseTier}" deactivated`); load(); }
    } catch (e) { erpToast.error(`Delete failed: ${e.message}`); }
  }, [load]);

  const onHardDelete = useCallback(async (row) => {
    if (!window.confirm(`HARD DELETE tier "${row.LicenseTier}"?\n\nThis PERMANENTLY removes the row AND drops the matrix tier column (T_${row.LicenseTier.replace(/\s+/g, '')}) from UAMS_Modules / SubModules / Screens.\n\nWill refuse if any client is on this tier. Cannot be undone.`)) return;
    try {
      const res = await apiFetch(`${API}/licensor/pricing/${encodeURIComponent(row.PricingId)}/hard`, { method: 'DELETE' });
      const data = await res.json();
      if (res.status === 409 && data.error === 'TIER_IN_USE') {
        erpToast.error(`Cannot hard-delete: ${data.clientCount} client(s), ${data.activeLicenses} active license(s) on this tier. Deactivate instead.`);
        return;
      }
      if (!res.ok || data.error) {
        erpToast.error(`Hard-delete failed: ${data.error || res.status}`);
      } else {
        erpToast.success(`Tier "${row.LicenseTier}" permanently deleted (${data.columnsDropped}/3 matrix columns dropped)`);
        load();
      }
    } catch (e) { erpToast.error(`Hard-delete failed: ${e.message}`); }
  }, [load]);

  const { onRowContextMenu, ContextMenuPortal } = useERPContextMenu({
    idField: 'PricingId',
    copyKeys: [
      { field: 'LicenseTier', label: 'Copy Tier Name' },
      { field: 'PricingId',   label: 'Copy Pricing ID' }
    ],
    onView:    startView,
    onEdit:    startEdit,
    onPreview: (r) => {
      const lines = Object.entries(r).map(([k, v]) => `${k}: ${v ?? ''}`).join('\n');
      const w = window.open('', '_blank');
      w.document.write(`<pre style="font-family:monospace;font-size:12px;padding:20px;white-space:pre-wrap">${lines.replace(/</g, '&lt;')}</pre>`);
    },
    onPrint:   () => window.print(),
    onExport:  (r) => {
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${r.LicenseTier || 'tier'}.json`; a.click();
    },
    extraItems: [
      { label: 'Duplicate',   onClick: startDuplicate, testId: 'ctx-duplicate' },
      { label: 'Deactivate',  onClick: onDelete,       testId: 'ctx-deactivate' },
      { label: 'Hard Delete', onClick: onHardDelete,   testId: 'ctx-hard-delete', danger: true }
    ]
  });

  return (
    <div data-testid="license-pricing-manager" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', borderRadius: 8,
        background: 'var(--erp-info-bg, #eff6ff)',
        border: '1px solid var(--erp-border, #bfdbfe)',
        fontSize: 11, color: '#1e40af'
      }}>
        <Database size={14} />
        <div>
          <strong>License Pricing</strong> · Each tier auto-provisions a matrix column on Modules / SubModules / Screens so privileges can be configured immediately after creation. Soft-deletes preserve audit history.
        </div>
      </div>

      {error && (
        <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 11 }}>
          {error} <button onClick={load} className="erp-btn" style={{ marginLeft: 8 }}><RefreshCw size={11} /> Retry</button>
        </div>
      )}

      <ERPAdvancedGrid
        title={`License Pricing (${total})`}
        columns={COLUMNS}
        data={paginated}
        total={total}
        idField="PricingId"
        nameField="LicenseTier"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sortField={sortField}
        sortDir={sortDir}
        onSort={onSort}
        filters={filters}
        onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={loading}
        showAddNew={true}
        onAddNew={startAdd}
        onEdit={(row, mode) => mode === 'view' ? startView(row) : startEdit(row)}
        onPreview={startView}
        testId="license-pricing"
        cellRenderer={(field, _value, row) => {
          if (field !== '_actions') return null;
          return (
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="erp-btn"
                title="Deactivate (soft delete — sets IsActive=0)"
                data-testid={`row-deactivate-${row.PricingId}`}
                onClick={(e) => { e.stopPropagation(); onDelete(row); }}
                style={{ fontSize: 10, padding: '3px 8px', color: '#b45309', borderColor: '#fde68a' }}
              >Deactivate</button>
              <button
                type="button"
                className="erp-btn"
                title="Hard delete (removes row + drops T_Tier matrix columns; refuses if any client uses this tier)"
                data-testid={`row-hard-delete-${row.PricingId}`}
                onClick={(e) => { e.stopPropagation(); onHardDelete(row); }}
                style={{ fontSize: 10, padding: '3px 8px', color: '#fff', background: '#dc2626', borderColor: '#dc2626' }}
              >Delete</button>
            </div>
          );
        }}
      />

      <RowContextDelegate
        gridTestId="license-pricing"
        rows={paginated}
        idField="PricingId"
        onRowContextMenu={onRowContextMenu}
      />
      {ContextMenuPortal}

      {editing && (
        <PricingFormModal
          mode={editing.mode}
          row={editing.row}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

export function PricingFormModal({ mode, row, onClose, onSaved }) {
  const isView = mode === 'view';
  const isAdd  = mode === 'add' || mode === 'duplicate';

  // -1 in DB means "unlimited" for numeric limit fields — show blank in the form
  const toInput = (v) => (v === -1 || v === null || v === undefined) ? '' : v;

  const initial = useMemo(() => {
    if (row) {
      const base = {
        LicenseTier:        row.LicenseTier || '',
        LicenseType:        row.LicenseType || 'paid',
        DbModel:            row.DbModel || 'dedicated',
        TierDescription:    row.TierDescription || '',
        BasePrice:          row.BasePrice ?? 0,
        PricePerUser:       row.PricePerUser ?? 0,
        PricePerSystem:     row.PricePerSystem ?? 0,
        SupportPrice:       row.SupportPrice ?? 0,
        SupportPercentage:  row.SupportPercentage ?? 0,
        Currency:           row.Currency || 'INR',
        MaxUsers:           toInput(row.MaxUsers),
        MaxDevices:         toInput(row.MaxDevices),
        MaxCompanies:       toInput(row.MaxCompanies),
        MaxDays:            toInput(row.MaxDays),
        Features:           row.Features || '',
        IsFree:             row.IsFree === true || row.IsFree === 1,
        IsSalesDefined:     row.IsSalesDefined === true || row.IsSalesDefined === 1,
        IsActive:           row.IsActive !== false && row.IsActive !== 0
      };
      if (mode === 'duplicate') base.LicenseTier = (base.LicenseTier || '') + ' (copy)';
      return base;
    }
    return {
      LicenseTier: '', LicenseType: 'paid', DbModel: 'dedicated', TierDescription: '',
      BasePrice: 0, PricePerUser: 0, PricePerSystem: 0,
      SupportPrice: 0, SupportPercentage: 0,
      Currency: 'INR',
      MaxUsers: '', MaxDevices: '', MaxCompanies: '', MaxDays: '',
      Features: '',
      IsFree: false, IsSalesDefined: false, IsActive: true
    };
  }, [row, mode]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (isView) { onClose(); return; }
    setSaving(true);
    try {
      const numOrNull      = (v) => (v === '' || v == null) ? null : Number(v);
      const numOrUnlimited = (v) => (v === '' || v == null) ? -1   : Number(v);
      const payload = {
        LicenseTier:        form.LicenseTier,
        LicenseType:        form.LicenseType || 'paid',
        DbModel:            form.DbModel || 'dedicated',
        TierDescription:    form.TierDescription || null,
        BasePrice:          Number(form.BasePrice) || 0,
        PricePerUser:       Number(form.PricePerUser) || 0,
        PricePerSystem:     Number(form.PricePerSystem) || 0,
        SupportPrice:       Number(form.SupportPrice) || 0,
        SupportPercentage:  Number(form.SupportPercentage) || 0,
        Currency:           form.Currency || 'INR',
        MaxUsers:           numOrUnlimited(form.MaxUsers),
        MaxDevices:         numOrUnlimited(form.MaxDevices),
        MaxCompanies:       numOrNull(form.MaxCompanies),
        MaxDays:            numOrNull(form.MaxDays),
        Features:           form.Features || null,
        IsFree:             !!form.IsFree,
        IsSalesDefined:     !!form.IsSalesDefined,
        IsActive:           !!form.IsActive
      };
      let res;
      if (isAdd) {
        res = await apiFetch(`${API}/licensor/pricing`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } else {
        res = await apiFetch(`${API}/licensor/row/LicensePricing/${encodeURIComponent(row.PricingId)}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      const data = await res.json();
      if (!res.ok || data.error) erpToast.error(`${isAdd ? 'Create' : 'Update'} failed: ${data.error || res.status}`);
      else { erpToast.success(`Tier ${isAdd ? 'created' : 'updated'}`); onSaved(); }
    } catch (e) { erpToast.error(`Save failed: ${e.message}`); }
    setSaving(false);
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
      onClick={onClose}
      data-testid="license-pricing-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg-surface, #fff)', color: 'var(--text-primary, #0f172a)', borderRadius: 10, padding: 20, minWidth: 620, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 30px 60px rgba(15,23,42,0.4)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag size={18} /> {isAdd ? 'Add Pricing Tier' : isView ? 'View Pricing Tier' : 'Edit Pricing Tier'}
          </h3>
          <button type="button" onClick={onClose} className="erp-btn" style={{ padding: '4px 10px' }} data-testid="license-pricing-cancel">✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          <Field label="Tier Name" required span={2}>            <input className="form-input" value={form.LicenseTier}
              onChange={(e) => setF('LicenseTier', e.target.value)}
              disabled={isView} required
              data-testid="pricing-form-tier" />
          </Field>
          <Field label="License Type" required>
            <select className="form-input" value={form.LicenseType}
              onChange={(e) => {
                const t = e.target.value;
                setF('LicenseType', t);
                // Convenience: sync IsFree/IsSalesDefined to match
                if (t === 'free') { setF('IsFree', true); setF('IsSalesDefined', false); }
                else if (t === 'demo') { setF('IsFree', false); setF('IsSalesDefined', true); }
                else { setF('IsFree', false); setF('IsSalesDefined', false); }
              }}
              disabled={isView} required
              data-testid="pricing-form-license-type"
              style={{ fontWeight: 700 }}
            >
              <option value="paid">Paid</option>
              <option value="free">Free</option>
              <option value="demo">Demo / Trial</option>
            </select>
          </Field>
          <Field label="Currency">
            <input className="form-input" value={form.Currency}
              onChange={(e) => setF('Currency', e.target.value.toUpperCase())}
              disabled={isView}
              data-testid="pricing-form-currency" />
          </Field>
          <Field label="DB Model">
            <select className="form-input" value={form.DbModel}
              onChange={(e) => setF('DbModel', e.target.value)}
              disabled={isView}
              data-testid="pricing-form-dbmodel">
              <option value="shared">shared</option>
              <option value="dedicated">dedicated</option>
              <option value="dedicated-isolated">dedicated-isolated</option>
            </select>
          </Field>

          <Field label="Base Price">
            <input className="form-input" type="number" value={form.BasePrice}
              onChange={(e) => setF('BasePrice', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              data-testid="pricing-form-base" />
          </Field>
          <Field label="₹ / User">
            <input className="form-input" type="number" value={form.PricePerUser}
              onChange={(e) => setF('PricePerUser', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              data-testid="pricing-form-user" />
          </Field>
          <Field label="₹ / System">
            <input className="form-input" type="number" value={form.PricePerSystem}
              onChange={(e) => setF('PricePerSystem', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              data-testid="pricing-form-system" />
          </Field>

          <Field label="Support Price">
            <input className="form-input" type="number" value={form.SupportPrice}
              onChange={(e) => setF('SupportPrice', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              data-testid="pricing-form-support-price" />
          </Field>
          <Field label="Support %">
            <input className="form-input" type="number" value={form.SupportPercentage}
              onChange={(e) => setF('SupportPercentage', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              data-testid="pricing-form-support-pct" />
          </Field>
          <Field label="Max Days (for trial)">
            <input className="form-input" type="number" value={form.MaxDays}
              onChange={(e) => setF('MaxDays', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              data-testid="pricing-form-maxdays" />
          </Field>

          <Field label="Max Users" span={1}>
            <input className="form-input" type="number" value={form.MaxUsers}
              onChange={(e) => setF('MaxUsers', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              placeholder="Blank = unlimited"
              data-testid="pricing-form-maxusers" />
          </Field>
          <Field label="Max Devices">
            <input className="form-input" type="number" value={form.MaxDevices}
              onChange={(e) => setF('MaxDevices', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              placeholder="Blank = unlimited"
              data-testid="pricing-form-maxdevices" />
          </Field>
          <Field label="Max Companies">
            <input className="form-input" type="number" value={form.MaxCompanies}
              onChange={(e) => setF('MaxCompanies', e.target.value)}
              onFocus={(e) => e.target.select()}
              disabled={isView}
              placeholder="Blank = unlimited"
              data-testid="pricing-form-maxco" />
          </Field>

          <Field label="Description" span={4}>
            <textarea className="form-input" rows={2} value={form.TierDescription}
              onChange={(e) => setF('TierDescription', e.target.value)}
              disabled={isView}
              data-testid="pricing-form-desc" />
          </Field>
          <Field label="Features (text or comma-separated)" span={4}>
            <textarea className="form-input" rows={2} value={form.Features}
              onChange={(e) => setF('Features', e.target.value)}
              disabled={isView}
              placeholder='e.g. {"reports":true,"sso":false}'
              data-testid="pricing-form-features" />
          </Field>

          <Field label="Free Tier">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
              <input type="checkbox" checked={!!form.IsFree}
                onChange={(e) => setF('IsFree', e.target.checked)}
                disabled={isView}
                data-testid="pricing-form-free" />
              <span style={{ fontSize: 12 }}>Free</span>
            </label>
          </Field>
          <Field label="Sales-Defined">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
              <input type="checkbox" checked={!!form.IsSalesDefined}
                onChange={(e) => setF('IsSalesDefined', e.target.checked)}
                disabled={isView}
                data-testid="pricing-form-sales" />
              <span style={{ fontSize: 12 }}>Custom quote</span>
            </label>
          </Field>
          <Field label="Active">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
              <input type="checkbox" checked={!!form.IsActive}
                onChange={(e) => setF('IsActive', e.target.checked)}
                disabled={isView}
                data-testid="pricing-form-active" />
              <span style={{ fontSize: 12 }}>{form.IsActive ? 'Active' : 'Inactive'}</span>
            </label>
          </Field>
        </div>

        {!isView && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onClose} className="erp-btn" disabled={saving} data-testid="pricing-form-cancel-btn">Cancel</button>
            <button type="submit" className="erp-btn erp-btn-primary" disabled={saving} data-testid="pricing-form-save">
              {saving ? 'Saving…' : (isAdd ? <><Plus size={12} /> Create</> : 'Save')}
            </button>
          </div>
        )}
        {isView && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type="button" onClick={onClose} className="erp-btn erp-btn-primary" data-testid="pricing-form-close-view">Close</button>
          </div>
        )}
      </form>
    </div>
  );
}

function Field({ label, required, span = 1, children }) {
  return (
    <div style={{ gridColumn: `span ${span}` }}>
      <label style={{ display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function RowContextDelegate({ gridTestId, rows, idField, onRowContextMenu }) {
  const ref = useRef({ rows, idField });
  ref.current = { rows, idField };
  useEffect(() => {
    const handler = (e) => {
      const tr = e.target.closest(`tr[data-testid^="${gridTestId}-row-"]`);
      if (!tr) return;
      const idAttr = tr.getAttribute('data-testid');
      const id = idAttr.replace(`${gridTestId}-row-`, '');
      const row = ref.current.rows.find(r => String(r[ref.current.idField]) === id);
      if (row) onRowContextMenu(e, row);
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, [gridTestId, onRowContextMenu]);
  return null;
}
